import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Heart, Stethoscope, Monitor, TreePine, CheckCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

const focusAreas = [
  {
    icon: Sparkles,
    title: "Spiritual & Mental Wellness",
    color: "#E28743",
    objective: "To promote mental well-being, emotional balance, and inner awareness through meditation and mindfulness practices.",
    why: "In today's fast-paced world, stress, anxiety, and emotional imbalance are rising. Without inner stability, external progress remains incomplete.",
    approach: [
      "Meditation awareness sessions and workshops",
      "Introduction to Vipassana and mindfulness practices",
      "Community-based stress management programs",
      "Awareness campaigns on mental well-being",
    ],
  },
  {
    icon: Heart,
    title: "Social Welfare & Inclusion",
    color: "#606C38",
    objective: "To ensure that individuals, especially vulnerable groups, have access to social security schemes and essential support systems.",
    why: "Many eligible citizens remain deprived of benefits due to lack of awareness, documentation, or guidance.",
    approach: [
      "Awareness drives on government schemes and yojanas",
      "Assistance with pension applications (old-age, widow, etc.)",
      "Documentation and facilitation support",
      "Community help centres for guidance",
    ],
  },
  {
    icon: Stethoscope,
    title: "Healthcare Access & Preventive Well-being",
    color: "#8ECAE6",
    objective: "To improve access to basic healthcare and promote preventive health awareness.",
    why: "Healthcare remains inaccessible or unaffordable for many, especially in rural and semi-urban areas.",
    approach: [
      "Free health check-up camps",
      "Preventive healthcare awareness drives",
      "Collaborations with doctors and healthcare professionals",
      "Promotion of holistic wellness practices",
    ],
  },
  {
    icon: Monitor,
    title: "Digital & Technical Empowerment",
    color: "#E28743",
    objective: "To bridge the digital divide and empower individuals with essential technological skills.",
    why: "Digital literacy is no longer optional — it is essential for education, employment, and access to services.",
    approach: [
      "Basic computer and smartphone training",
      "Digital awareness workshops for youth and women",
      "Online safety and digital literacy programs",
      "Skill awareness for emerging technologies",
    ],
  },
  {
    icon: TreePine,
    title: "Holistic Community Development",
    color: "#606C38",
    objective: "To build sustainable, balanced, and self-reliant communities.",
    why: "Long-term development requires harmony between people, environment, and lifestyle.",
    approach: [
      "Tree plantation and environmental initiatives",
      "Cleanliness and sustainability drives",
      "Awareness on balanced lifestyle and nutrition",
      "Community engagement programs",
    ],
  },
];

function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsVisible(true);
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, isVisible];
}

function AnimatedSection({ children, className = "", delay = 0 }) {
  const [ref, isVisible] = useInView();
  return (
    <div ref={ref} className={`${className} transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

export default function FocusAreasPage() {
  return (
    <div data-testid="focus-areas-page" className="pt-20">
      {/* SECTION 1 — INTRODUCTION */}
      <section className="py-20 sm:py-28 bg-[#F3F0E6]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <AnimatedSection>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#1B201E] mb-6">
              Our Focus Areas
            </h1>
            <p className="text-[#7A827E] text-base sm:text-lg max-w-4xl leading-relaxed">
              At BSM Welfare Foundation, our work is guided by a holistic vision — addressing both the inner well-being of individuals and the external challenges faced by communities. Rather than operating in isolated domains, we focus on interconnected areas that together create sustainable and meaningful social transformation.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* OVERVIEW STATEMENT */}
      <section data-testid="overview-statement" className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <AnimatedSection className="max-w-3xl mx-auto text-center">
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1B201E] mb-5">A Holistic Model of Impact</h2>
            <p className="text-[#7A827E] text-base leading-relaxed">
              We believe that true development is achieved when mental clarity, social security, healthcare access, and technical empowerment progress together. Our focus areas are designed to build aware individuals, resilient communities, and a balanced society.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* FOCUS AREAS DETAILED */}
      <section data-testid="focus-areas-detailed" className="pb-20 sm:pb-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 space-y-12">
          {focusAreas.map((area, i) => (
            <AnimatedSection key={i}>
              <Card
                data-testid={`focus-area-detail-${i}`}
                className="bg-white border-0 rounded-3xl overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.04)] transition-all duration-500 hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)]"
              >
                <CardContent className="p-8 sm:p-10">
                  <div className="flex items-start gap-5 mb-6">
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${area.color}15` }}>
                      <area.icon size={26} style={{ color: area.color }} strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-['Outfit'] text-2xl font-semibold text-[#1B201E] mb-1">{area.title}</h3>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div>
                      <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#E28743] mb-2">Objective</p>
                      <p className="text-[#7A827E] text-sm leading-relaxed">{area.objective}</p>
                    </div>
                    <div>
                      <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#E28743] mb-2">Why It Matters</p>
                      <p className="text-[#7A827E] text-sm leading-relaxed">{area.why}</p>
                    </div>
                    <div>
                      <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#E28743] mb-2">Our Approach</p>
                      <ul className="space-y-2">
                        {area.approach.map((item, j) => (
                          <li key={j} className="flex items-start gap-2">
                            <CheckCircle size={14} className="text-[#606C38] mt-0.5 shrink-0" strokeWidth={1.5} />
                            <span className="text-[#7A827E] text-sm">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* CLOSING STATEMENT */}
      <section className="py-20 sm:py-28 bg-[#1B201E]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <AnimatedSection>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold tracking-tight text-white mb-5">Building a Balanced Future</h2>
            <p className="text-[#7A827E] text-base leading-relaxed max-w-2xl mx-auto mb-5">
              Each of our focus areas is interconnected — together forming a comprehensive model of development that nurtures both individuals and communities.
            </p>
            <p className="text-[#7A827E] text-base leading-relaxed max-w-2xl mx-auto">
              BSM Welfare Foundation is committed to creating impact that is not only visible, but also meaningful and sustainable.
            </p>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
